import { assign, createMachine } from 'xstate';
import { createLoanApp } from './builder';
import { ExitStrategyValue, LoanProductType, LoanType } from './const';
import { SECTION_STATES_DEFAULT } from './sectionStatesUtil';

import {
  StepStatus,
  UserQuoteProcessSteps,
  UserQuoteProcessStep,
  UserQuoteProcessStepNames,
  type ActionType,
  type AppUser,
  type LoanApplication,
  type UserQuoteProcessStatus,
  type UserQuoteProcessStepEnum,
  type UserQuoteProcessStepStatus,
} from '@/types';

import { useFeatureFlags } from './useFeatureFlags';

import type { SectionStates, SectionStateType } from '@/types/SectionState';
import type { UnaccreditedBrokerProfile } from '@/types/UnaccreditedBrokerProfile';

const { isCommercialQuoteFlowEnabled } = useFeatureFlags();

export interface UserQuoteMachineContext {
  loanApp: LoanApplication;
  startStateType: SectionStateType | null;
  sectionStates: SectionStates;
  unaccreditedBroker?: UnaccreditedBrokerProfile;
  from?: string;
  authorisedUser: Record<string, string>;
  status: UserQuoteProcessStatus;
  current: UserQuoteProcessStepEnum;
}

export const initUserQuoteMachine = (params?: {
  actionType: ActionType;
  userType: AppUser;
  brokerId?: string;
}): UserQuoteMachineContext => ({
  loanApp: createLoanApp(params),
  startStateType: null,
  sectionStates: { ...SECTION_STATES_DEFAULT },
  unaccreditedBroker: undefined,
  authorisedUser: {},
  status: Object.fromEntries(
    UserQuoteProcessStepNames.map((step) => [
      step,
      step === UserQuoteProcessStep.VerifyIdentity ? StepStatus.Ready : StepStatus.Incomplete,
    ]),
  ) as UserQuoteProcessStatus,
  current: UserQuoteProcessStep.VerifyIdentity,
});

export type UserQuoteMachineEvent =
  | {
      type: 'NEXT';
      loanApp: LoanApplication;
      processSteps?: UserQuoteProcessStepStatus[];
      nextStep?: UserQuoteProcessStepEnum;
    }
  | { type: 'UPDATE'; loanApp: LoanApplication }
  | { type: 'BACK' }
  | { type: 'READY' }
  | { type: 'GOTO'; step: UserQuoteProcessStepEnum }
  | { type: 'RELOAD'; state: UserQuoteMachineContext }
  | { type: 'RESTART' }
  | { type: 'RESTART_BROKER'; brokerId: string }
  | { type: 'NEXT_AND_SAVE_BROKER'; unaccreditedBroker: UnaccreditedBrokerProfile }
  | { type: 'UPDATE_START_STATE_TYPE'; value: SectionStateType }
  | { type: 'UPDATE_SECTION_STATE'; sectionKey: keyof SectionStates; value: SectionStateType }
  | { type: 'UPDATE_ALL_SECTION_STATES'; value: SectionStateType };

export const loanAppUserQuoteMachine = createMachine<UserQuoteMachineContext, UserQuoteMachineEvent>(
  {
    id: 'loanApplicationUserQuote',
    predictableActionArguments: true,
    schema: {
      context: {} as UserQuoteMachineContext,
    },
    context: initUserQuoteMachine(),
    initial: 'start',
    states: {
      start: {
        on: {
          NEXT: {
            target: 'gotoReady',
          },
        },
      },
      [UserQuoteProcessStep.VerifyIdentity]: {
        on: {
          NEXT: {
            target: 'checkUser',
            actions: 'saveData',
          },
        },
      },
      checkUser: {
        always: [
          {
            target: UserQuoteProcessStep.UnaccreditedBroker,
            cond: 'isUserUnaccreditedBroker',
          },
          {
            target: UserQuoteProcessStep.StartQuote,
          },
        ],
      },
      [UserQuoteProcessStep.UnaccreditedBroker]: {
        on: {
          NEXT_AND_SAVE_BROKER: {
            target: UserQuoteProcessStep.StartQuote,
            actions: 'saveBrokerData',
          },
        },
      },
      [UserQuoteProcessStep.StartQuote]: {
        on: {
          NEXT: [
            {
              target: UserQuoteProcessStep.SelectLoanType,
              actions: 'saveData',
            },
          ],
        },
      },
      [UserQuoteProcessStep.SelectLoanType]: {
        on: {
          NEXT: [
            {
              cond: (ctx, evt) => evt.loanApp.loanType === LoanType.SINGLE_SECURITY,
              target: 'checkPurpose',
              actions: 'saveData',
            },
            {
              target: 'checkPropertyOrder',
              actions: 'saveData',
            },
          ],
        },
      },
      checkPurpose: {
        always: [
          {
            target: 'checkPropertyOrder',
            cond: 'showLoanDetails',
          },
          {
            target: UserQuoteProcessStep.LoanPurpose,
          },
        ],
      },
      checkPropertyOrder: {
        always: [
          {
            target: UserQuoteProcessStep.OutgoingProperties,
            cond: (ctx) => ctx.loanApp.userType === 'Crm' && ctx.loanApp.loanType !== LoanType.PURCHASE_ONLY,
          },
          {
            target: UserQuoteProcessStep.IncomingProperties,
          },
        ],
      },
      [UserQuoteProcessStep.LoanPurpose]: {
        on: {
          NEXT: [
            {
              target: UserQuoteProcessStep.IncomingProperties,
              cond: (_, evt) => evt.type === 'NEXT' && evt.nextStep === UserQuoteProcessStep.IncomingProperties,
              actions: 'saveData',
            },
            {
              target: UserQuoteProcessStep.OutgoingProperties,
              cond: (ctx) => ctx.loanApp.loanType === LoanType.SINGLE_SECURITY,
              actions: 'saveData',
            },
            {
              target: 'checkPropertyOrder',
              actions: 'saveData',
            },
          ],
        },
      },
      [UserQuoteProcessStep.IncomingProperties]: {
        on: {
          NEXT: [
            {
              cond: (ctx) => ctx.loanApp.loanType === LoanType.PURCHASE_ONLY,
              target: 'checkLoanDetails',
              actions: 'saveData',
            },
            {
              cond: 'isCrmUser',
              target: UserQuoteProcessStep.LoanDetails,
              actions: 'saveData',
            },
            {
              target: UserQuoteProcessStep.OutgoingProperties,
              actions: 'saveData',
            },
          ],
        },
      },
      [UserQuoteProcessStep.OutgoingProperties]: {
        on: {
          NEXT: [
            {
              cond: (ctx) => ctx.loanApp.userType === 'Crm' && ctx.loanApp.loanType !== LoanType.SINGLE_SECURITY,
              target: UserQuoteProcessStep.IncomingProperties,
              actions: 'saveData',
            },
            {
              cond: (ctx, evt) =>
                evt.loanApp.loanType === LoanType.SINGLE_SECURITY || evt.loanApp.loanType === LoanType.PURCHASE_ONLY,
              target: 'checkLoanDetails',
              actions: 'saveData',
            },
            {
              target: UserQuoteProcessStep.LoanDetails,
              actions: 'saveData',
            },
          ],
        },
      },
      checkLoanDetails: {
        always: [
          {
            target: UserQuoteProcessStep.LoanDetails,
            cond: (ctx) =>
              ctx.loanApp.loanType === LoanType.BRIDGING || ctx.loanApp.loanType === LoanType.PURCHASE_ONLY,
          },
          {
            target: UserQuoteProcessStep.ApplicantDetails,
          },
        ],
      },
      [UserQuoteProcessStep.LoanDetails]: {
        on: {
          NEXT: {
            target: UserQuoteProcessStep.ApplicantDetails,
            actions: 'saveData',
          },
        },
      },
      [UserQuoteProcessStep.ApplicantDetails]: {
        on: {
          NEXT: {
            target: UserQuoteProcessStep.PropertyOwnership,
            actions: 'saveData',
          },
        },
      },
      [UserQuoteProcessStep.PropertyOwnership]: {
        on: {
          NEXT: {
            target: 'checkDebt',
            actions: 'saveData',
          },
        },
      },
      checkDebt: {
        always: [
          {
            target: UserQuoteProcessStep.Income,
            cond: 'showIncomeExpenses',
          },
          {
            target: UserQuoteProcessStep.Assets,
            cond: 'showAssets',
          },
          {
            target: UserQuoteProcessStep.SubmitUserQuote,
          },
        ],
      },
      [UserQuoteProcessStep.Income]: {
        on: {
          NEXT: {
            target: 'checkExpenses',
            actions: 'saveData',
          },
        },
      },
      checkExpenses: {
        always: [
          {
            target: UserQuoteProcessStep.Expenses,
            cond: 'showExpenses',
          },
          {
            target: UserQuoteProcessStep.Assets,
          },
        ],
      },
      [UserQuoteProcessStep.Expenses]: {
        on: {
          NEXT: {
            target: UserQuoteProcessStep.Assets,
            actions: 'saveData',
          },
        },
      },
      [UserQuoteProcessStep.Assets]: {
        on: {
          NEXT: {
            target: UserQuoteProcessStep.SubmitUserQuote,
            actions: 'saveData',
          },
        },
      },
      [UserQuoteProcessStep.SubmitUserQuote]: {
        on: {
          NEXT: {
            target: UserQuoteProcessStep.Finished,
            actions: 'saveData',
          },
        },
      },
      [UserQuoteProcessStep.Finished]: {
        on: {
          NEXT: {
            target: UserQuoteProcessStep.Finished,
            actions: 'resetQuoteApplication',
          },
        },
      },
      prevStage: {
        always: [...UserQuoteProcessStepNames]
          .sort((a, b) => UserQuoteProcessSteps[b].order - UserQuoteProcessSteps[a].order)
          .map((step) => ({
            target: step,
            cond: (ctx) =>
              UserQuoteProcessSteps[ctx.current]?.order > UserQuoteProcessSteps[step].order &&
              ctx.status[step] === StepStatus.Completed &&
              !(
                (isCommercialQuoteFlowEnabled() || ctx.loanApp.loanType === LoanType.PURCHASE_ONLY) &&
                step === UserQuoteProcessStep.OutgoingProperties
              ),
          })),
      },
      gotoReady: {
        always: UserQuoteProcessStepNames.map((step) => ({
          target: step,
          cond: (ctx) => ctx.status[step] === StepStatus.Ready,
        })),
      },
    },
    on: {
      BACK: {
        target: 'prevStage',
        cond: (ctx) => ctx.current !== UserQuoteProcessStep.VerifyIdentity,
      },
      READY: {
        target: 'gotoReady',
      },
      UPDATE: {
        actions: 'saveData',
      },
      GOTO: UserQuoteProcessStepNames.map((step) => ({
        target: step,
        cond: (ctx, evt) => evt.step === step,
      })),
      RELOAD: {
        target: 'gotoReady',
        actions: 'updateContext',
      },
      RESTART: {
        target: UserQuoteProcessStep.VerifyIdentity,
        actions: 'resetQuote',
      },
      RESTART_BROKER: {
        target: UserQuoteProcessStep.VerifyIdentity,
        actions: 'resetBrokerQuote',
      },
      UPDATE_START_STATE_TYPE: {
        actions: 'updateStartStateType',
      },
      UPDATE_SECTION_STATE: {
        actions: 'updateSectionState',
      },
      UPDATE_ALL_SECTION_STATES: {
        actions: 'updateAllSectionStates',
      },
    },
  },
  {
    actions: {
      saveData: assign((context, event) => {
        if (event.type !== 'NEXT' && event.type !== 'UPDATE') {
          return {};
        }

        const effectiveProcessSteps =
          event.type === 'NEXT' && event.processSteps
            ? Object.fromEntries(event.processSteps.map((p) => [p.id, p]))
            : UserQuoteProcessSteps;

        return {
          loanApp: event.loanApp,
          sectionStates: context.sectionStates,
          status: Object.fromEntries(
            Object.entries(context.status).map(([step, status]) => {
              const stepId = step as UserQuoteProcessStepEnum;
              return [
                step,
                step === context.current
                  ? StepStatus.Completed
                  : effectiveProcessSteps[stepId].order < effectiveProcessSteps[context.current].order
                  ? status
                  : StepStatus.Incomplete,
              ];
            }),
          ) as UserQuoteProcessStatus,
        };
      }),
      saveBrokerData: assign((context, event) => {
        if (event.type === 'NEXT_AND_SAVE_BROKER') {
          return {
            unaccreditedBroker: event.unaccreditedBroker,
            status: Object.fromEntries(
              Object.entries(context.status).map(([step, status]) => [
                step,
                step === context.current
                  ? StepStatus.Completed
                  : UserQuoteProcessSteps[step as UserQuoteProcessStepEnum].order <
                    UserQuoteProcessSteps[context.current].order
                  ? status
                  : StepStatus.Incomplete,
              ]),
            ) as UserQuoteProcessStatus,
          };
        }
        return {};
      }),
      updateContext: assign((context, event) => (event.type === 'RELOAD' ? event.state : {})),
      resetQuote: assign(() => initUserQuoteMachine({ userType: 'UnaccreditedBroker', actionType: 'createNew' })),
      resetBrokerQuote: assign((context, event) =>
        initUserQuoteMachine({
          userType: 'UnaccreditedBroker',
          brokerId: event.type === 'RESTART_BROKER' ? event.brokerId : '',
          actionType: 'createNew',
        }),
      ),
      updateStartStateType: assign((context, event) => {
        return event.type === 'UPDATE_START_STATE_TYPE' ? { startStateType: event.value } : {};
      }),
      updateSectionState: assign((context, event) => {
        return event.type === 'UPDATE_SECTION_STATE'
          ? {
              sectionStates: {
                ...context.sectionStates,
                [event.sectionKey]: event.value,
              },
            }
          : {};
      }),
      updateAllSectionStates: assign((context, event) => {
        if (event.type === 'UPDATE_ALL_SECTION_STATES') {
          const updatedStates = Object.keys(context.sectionStates).reduce((acc, key) => {
            acc[key as keyof SectionStates] = event.value;
            return acc;
          }, {} as SectionStates);
          return {
            sectionStates: updatedStates,
          };
        }
        return {};
      }),
    },
    guards: {
      hasEndDebt: (ctx) => ctx.loanApp.endDebt > 0,
      showIncomeExpenses: (ctx) => {
        const isCommercial = ctx.loanApp.productType === LoanProductType.COMMERCIAL;
        const hasEndDebt = ctx.loanApp.endDebt > 0;
        const isSingleSecurity = ctx.loanApp.loanType === LoanType.SINGLE_SECURITY;
        const isFromCrm = ctx.from === 'crm';

        // CRM always shows income/expenses (including commercial); check before commercial guard
        if (isFromCrm) return true;

        // TODO: Remove this check to re-enable income/expenses for commercial applications.
        // Temporarily skipping all financial collection for commercial go-live.
        // When re-enabling, also restore the isCommercial check in showAssets below.
        if (isCommercial) return false;

        // No end debt scenarios: skip income/expenses
        // For Single Security + No End Debt + Exit=Sale: goes directly to assets (assets only scenario)
        // For all other no end debt cases: skip financial collection entirely
        if (!hasEndDebt) return false;

        // End debt scenarios: show income
        // For Single Security + End Debt + security refinance exit strategy
        if (isSingleSecurity && ctx.loanApp.exitStrategy === ExitStrategyValue.SECURITY_REFINANCE) {
          return true;
        }

        // For bridging loans with end debt: show income
        if (!isSingleSecurity) return true;

        // Single Security + End Debt + other exit strategies: still show income
        return hasEndDebt;
      },
      showAssets: (ctx) => {
        return ctx.loanApp.loanType === LoanType.SINGLE_SECURITY || ctx.loanApp.loanType === LoanType.PURCHASE_ONLY;
      },
      showLoanDetails: (ctx) => ctx.loanApp.loanType === LoanType.BRIDGING,
      isUserUnaccreditedBroker: (ctx) => ctx.loanApp.userType === 'UnaccreditedBroker',
      isCrmUser: (ctx) => ctx.loanApp.userType === 'Crm',
      showExpenses: (ctx) => {
        if (ctx.loanApp.userType === 'Crm' || ctx.from === 'crm') {
          return true;
        }
        return ctx.loanApp.assessmentType !== 'unregulated';
      },
    },
  },
);
